import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { HeroBadge } from '../sections/HeroBadge.jsx';
import { ArrowIcon } from '../sections/ArrowIcon.jsx';
import { CheckCircle, CrossCircle } from '../sections/CheckCircle.jsx';
import { INK, FG_2, FG_3, LINE, PURPLE, PRIMARY_TINT, FONTS } from '../sections/theme.js';
import { useLocalizedPath } from '../../hooks/useLocalizedPath.jsx';

const SR_ONLY = {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
};

export default function Purpose() {
    const { t } = useTranslation();
    const { compareCredly, compareAccredible, compareBadgr, compareAcreditta, compareCertifier } = useLocalizedPath();

    const rows = t('home.purpose-rows', { returnObjects: true });

    const compareLinks = [
        { label: t('home.purpose-compare-credly'), href: compareCredly() },
        { label: t('home.purpose-compare-accredible'), href: compareAccredible() },
        { label: t('home.purpose-compare-badgr'), href: compareBadgr() },
        { label: t('home.purpose-compare-acreditta'), href: compareAcreditta() },
        { label: t('home.purpose-compare-certifier'), href: compareCertifier() },
    ];

    const cellBase = {
        py: { xs: 2, md: 2.75 },
        px: { xs: 1.5, md: 2.75 },
        fontSize: { xs: '0.8rem', md: '0.875rem' },
        verticalAlign: 'middle',
    };

    return (
        <Box component="section" id="por-que-pok" sx={{ background: '#fff', py: { xs: 6, md: 9 } }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', maxWidth: 660, mx: 'auto', mb: 5 }}>
                    <HeroBadge label={t('home.purpose-tag')} />
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.875rem', md: '2.25rem' },
                            fontWeight: 700,
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            color: INK,
                            mb: 1.75,
                        }}
                    >
                        {t('home.purpose-title2')}
                    </Typography>
                    <Typography sx={{ fontSize: '1.0625rem', fontWeight: 400, color: FG_2, lineHeight: 1.55 }}>
                        {t('home.purpose-subtitle')}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        maxWidth: 1040,
                        mx: 'auto',
                        border: `1px solid ${LINE}`,
                        borderRadius: '22px',
                        overflow: 'hidden',
                        overflowX: 'auto',
                        boxShadow: '0 32px 70px -24px rgba(20,11,61,.42), 0 8px 24px -12px rgba(20,11,61,.18)',
                    }}
                >
                    <Box
                        component="table"
                        sx={{
                            width: '100%',
                            minWidth: 560,
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            tableLayout: 'fixed',
                        }}
                    >
                        <Box component="caption" sx={SR_ONLY}>
                            {t('home.purpose-title2')}
                        </Box>
                        <Box component="thead">
                            <Box component="tr">
                                <Box component="th" scope="col" sx={{ width: '46%' }} />
                                <Box
                                    component="th"
                                    scope="col"
                                    sx={{
                                        width: '27%',
                                        background: PURPLE,
                                        color: '#fff',
                                        py: 2.5,
                                        px: 2,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Box sx={{ fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.01em' }}>
                                        POK
                                    </Box>
                                    <Box
                                        sx={{
                                            fontFamily: FONTS.mono,
                                            fontSize: '0.6rem',
                                            fontWeight: 500,
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                            color: 'rgba(255,255,255,.7)',
                                            mt: 0.4,
                                        }}
                                    >
                                        {t('home.purpose-pok-sub')}
                                    </Box>
                                </Box>
                                <Box
                                    component="th"
                                    scope="col"
                                    sx={{
                                        width: '27%',
                                        background: '#f4f4f6',
                                        color: FG_2,
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        textAlign: 'center',
                                        px: 2,
                                    }}
                                >
                                    {t('home.purpose-col-others')}
                                </Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {Array.isArray(rows) &&
                                rows.map((row, i) => (
                                    <Box component="tr" key={i}>
                                        <Box
                                            component="th"
                                            scope="row"
                                            sx={{
                                                ...cellBase,
                                                textAlign: 'left',
                                                fontWeight: 600,
                                                color: INK,
                                                borderTop: `1px solid ${LINE}`,
                                            }}
                                        >
                                            {row.criteria}
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{
                                                ...cellBase,
                                                background: PRIMARY_TINT,
                                                borderTop: `1px solid ${LINE}`,
                                                color: INK,
                                                fontWeight: 500,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                                                <CheckCircle />
                                                {row.pok}
                                            </Box>
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{ ...cellBase, color: FG_3, borderTop: `1px solid ${LINE}` }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                                                <CrossCircle />
                                                {row.others}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography
                        sx={{
                            fontFamily: FONTS.mono,
                            fontSize: '0.66rem',
                            fontWeight: 500,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: FG_3,
                            mb: 2,
                        }}
                    >
                        {t('home.purpose-compare-title')}
                    </Typography>
                    <Stack
                        direction="row"
                        sx={{
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: 1.25,
                        }}
                    >
                        {compareLinks.map((link) => (
                            <Box
                                key={link.href}
                                component="a"
                                href={link.href}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.6,
                                    fontSize: '0.84rem',
                                    fontWeight: 600,
                                    color: PURPLE,
                                    background: '#fff',
                                    border: '1px solid rgba(81,36,166,0.22)',
                                    px: 2,
                                    py: 1.1,
                                    borderRadius: '8px',
                                    textDecoration: 'none',
                                    transition: 'background .15s',
                                    '&:hover': { background: 'rgba(81,36,166,0.06)' },
                                }}
                            >
                                {link.label}
                                <ArrowIcon size={16} />
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
}
